/**
 * Task #6436 — dismissible "Instant Book now available" signpost card on
 * the host dashboard.
 *
 * Shown only when:
 *   - the `feature_instant_book` flag is ON, and
 *   - the host has at least one listing that offers self-drive
 *     (`rentalDrivingActive`) and does NOT already have Instant Book
 *     enabled, and
 *   - the host hasn't dismissed the card before.
 *
 * The CTA deep-links to the pricing tab of the listing wizard for a
 * sensibly-chosen car: a live self-drive listing without Instant Book is
 * preferred; if none is live yet, any self-drive listing without Instant
 * Book is used as the fallback target.
 *
 * Dismissal persists client-side in localStorage under a per-user
 * namespaced key (`bac.instantBookSignpost.dismissed.v1.<userId>`) so
 * another host on the same browser still sees the card. Server-side
 * persistence is intentionally out of scope.
 */
import { useState } from "react";
import { Link } from "wouter";
import { Zap, X, ArrowRight } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { useDashboardUserId } from "@/hooks/dashboard-queries";
import { isListingLive } from "@/lib/listing-status";
import type { OwnerListing } from "@/lib/listings-api";

const DISMISS_KEY_PREFIX = "bac.instantBookSignpost.dismissed.v1";

export function instantBookSignpostDismissKey(userId: string | null): string {
  return `${DISMISS_KEY_PREFIX}.${userId ?? "anon"}`;
}

function readDismissed(userId: string | null): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(instantBookSignpostDismissKey(userId)) === "1";
  } catch {
    return false;
  }
}

/**
 * Picks the listing the CTA should deep-link to: prefer a live self-drive
 * listing without Instant Book; fall back to any self-drive listing
 * without Instant Book. Returns null when the host has no eligible
 * listing (card hidden).
 */
export function pickInstantBookSignpostTarget(
  listings: OwnerListing[],
): OwnerListing | null {
  const candidates = listings.filter(
    (l) => l.rentalDrivingActive && !l.instantBookActive,
  );
  if (candidates.length === 0) return null;
  return candidates.find((l) => isListingLive(l)) ?? candidates[0];
}

export function InstantBookSignpost({ listings }: { listings: OwnerListing[] }) {
  const { t } = useAppTranslation();
  const instantBookEnabled = useFeatureFlag("feature_instant_book");
  const userId = useDashboardUserId();
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(userId));

  if (!instantBookEnabled || dismissed) return null;

  const target = pickInstantBookSignpostTarget(listings);
  if (!target) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(instantBookSignpostDismissKey(userId), "1");
    } catch {
      // localStorage may be unavailable (private mode / blocked); fail soft —
      // the card just re-appears next visit.
    }
    setDismissed(true);
  };

  const bullets = [
    t("dashboard.instantBookSignpost.bullet_1", "Renters can book instantly, with no waiting on approvals"),
    t("dashboard.instantBookSignpost.bullet_2", "Instant Book listings rank higher in search"),
    t("dashboard.instantBookSignpost.bullet_3", "More visibility means more booking requests"),
  ];

  return (
    <Alert
      variant="success"
      icon={Zap}
      aria-labelledby="instant-book-signpost-title"
      data-testid="instant-book-signpost"
      className="mb-6"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <AlertTitle id="instant-book-signpost-title">
            {t("dashboard.instantBookSignpost.title", "Instant Book is now available")}
          </AlertTitle>
          <AlertDescription>{bullets.join(" · ")}</AlertDescription>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            href={`/dashboard/listings/${target.id}/edit?tab=pricing`}
            data-testid="instant-book-signpost-cta"
            className="cursor-pointer inline-flex shrink-0 items-center gap-2 rounded-lg border border-brand px-4 py-2 text-sm font-medium text-brand transition hover:bg-brand/5"
          >
            {t("dashboard.instantBookSignpost.cta", "Turn on Instant Book")}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t("dashboard.instantBookSignpost.dismiss_aria", "Dismiss")}
            data-testid="instant-book-signpost-dismiss"
            className="cursor-pointer inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-green-900/60 transition-colors hover:bg-green-900/10 hover:text-green-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </Alert>
  );
}

export default InstantBookSignpost;
